import { ArrowUpRight, Disc3, Music2 } from "lucide-react";
import { useEffect, useState } from "react";
import { apiBaseUrl } from "./config";
import { fallbackReleases } from "./releases";
import "./release-styles.css";

export type MusicRelease = {
  id?: string;
  slug: string;
  title: string;
  releaseType: string;
  coverArtUrl?: string | null;
  releaseDate?: string | null;
  description: string;
  spotifyUrl?: string | null;
  appleMusicUrl?: string | null;
  youtubeUrl?: string | null;
  amazonMusicUrl?: string | null;
  lyrics?: string | null;
  featured?: boolean;
};

function releaseDateLabel(value?: string | null) {
  if (!value) return "Release";

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(value));
}

export function ReleaseCatalog() {
  const [releases, setReleases] = useState<MusicRelease[]>(fallbackReleases);
  const [status, setStatus] = useState<"loading" | "ready" | "fallback">("loading");

  useEffect(() => {
    let active = true;

    async function loadReleases() {
      try {
        const response = await fetch(`${apiBaseUrl}/api/releases`);

        if (!response.ok) throw new Error("Could not load releases");

        const payload = (await response.json()) as { releases?: MusicRelease[] };
        const nextReleases = payload.releases?.length ? payload.releases : fallbackReleases;

        if (active) {
          setReleases(nextReleases);
          setStatus("ready");
        }
      } catch {
        if (active) {
          setReleases(fallbackReleases);
          setStatus("fallback");
        }
      }
    }

    loadReleases();

    return () => {
      active = false;
    };
  }, []);

  return (
    <section className="releases-band" id="releases" aria-labelledby="releases-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Official Catalog</p>
          <h2 id="releases-title">Music releases</h2>
        </div>
        <p className="section-kicker">
          {status === "loading" ? "Loading catalog" : "Singles, projects, lyrics, and streaming links."}
        </p>
      </div>
      <div className="release-grid">
        {releases.map((release) => (
          <article className="release-card release-card-rich" key={release.slug}>
            <div className="release-art" aria-hidden="true">
              {release.coverArtUrl ? <img src={release.coverArtUrl} alt="" /> : <Disc3 size={36} />}
            </div>
            <div className="release-body">
              <p className="release-meta">
                <span>{release.releaseType}</span>
                <span>{releaseDateLabel(release.releaseDate)}</span>
              </p>
              <h3>{release.title}</h3>
              <p>{release.description}</p>
              <div className="release-actions">
                <a href={`/releases/${release.slug}`}>
                  <Music2 size={16} aria-hidden="true" />
                  Details
                </a>
                {release.spotifyUrl && (
                  <a href={release.spotifyUrl} target="_blank" rel="noreferrer">
                    Stream
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
      {status === "fallback" && <p className="catalog-note">Showing starter catalog while the release API is unavailable.</p>}
    </section>
  );
}
